import * as XLSX from 'xlsx';

export const generateQuotationExcel = (quotationData, companyData = null) => {
  try {
    // Format date helper
    const formatDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    // Create workbook
    const wb = XLSX.utils.book_new();
    
    // Prepare data for Excel
    const excelData = [];
    
    // Company Header
    excelData.push(['', '', '', 'QUOTATION']); // Centered QUOTATION
    excelData.push(['Faman Services & Solutions®']);
    excelData.push(['43/1582, Ground Floor, Kochapilly Road, Palarivattom']);
    excelData.push(['Ernakulam, Kerala – 682025, India']);
    excelData.push(['Email: support@faman.in | Website: www.faman.in | Ph No.: 9633940163']);
    excelData.push(['GSTIN: 32AAFFF1487P1Z3 | STATE: KERALA | STATE CODE: 32']);
    excelData.push([]);
    
    // Customer Info (Left) and Quotation Details (Right) - Proper Layout
    // Row 1: BILLED TO label in col A | Quotation Number label + value (right)
    excelData.push(['BILLED TO:', '', '', '', '', 'Quotation Number', quotationData.QAPrint?.InvoiceNo || '']);
    
    // Row 2: Customer Name in col B (next to BILLED TO) | Quotation Date label + value (right)
    excelData.push(['', quotationData.QAPrint?.CustomerName || '', '', '', '', 'Quotation Date', formatDate(quotationData.QAPrint?.QADate)]);
    
    // Row 3: Address1 in col B | Revision label + value (right)
    excelData.push(['', quotationData.QAPrint?.CustomerAddress1 || '', '', '', '', 'Revision', quotationData.QAPrint?.RevisionCount ? `Revision ${quotationData.QAPrint.RevisionCount}` : '']);
    
    // Row 4: Address2 in col B | Site label + value (right)
    if (quotationData.QAPrint?.CustomerAddress2) {
      excelData.push(['', quotationData.QAPrint.CustomerAddress2, '', '', '', 'Site', quotationData.QAPrint?.CustomerLocation || '']);
    }
    
    // Row 5: Location in col B
    excelData.push(['', quotationData.QAPrint?.CustomerLocation || '']);
    
    // GST Number row - Label in col A, value in col B
    if (quotationData.QAPrint?.CustomerGSTNumber) {
      excelData.push(['GST No:', quotationData.QAPrint.CustomerGSTNumber]);
    }
    
    excelData.push([]);
    
    // Get items from QAPrintList
    const tableData = quotationData.QAPrintList || [];
    
    // Check if IGST is used
    const isIGST = tableData.some(item => (item.IGSTAmount ?? 0) > 0);
    
    // Table Headers
    if (isIGST) {
      excelData.push(['Sl.No', 'Description', 'HSN', 'UOM', 'Qty', 'Unit Price', 'Taxable', 'GST %', 'IGST', 'Total']);
    } else {
      excelData.push(['Sl.No', 'Description', 'HSN', 'UOM', 'Qty', 'Unit Price', 'Taxable', 'GST %', 'CGST', 'SGST', 'Total']);
    }
    
    // Add items to Excel
    tableData.forEach((item, index) => {
      // Build description: WorkItemName / WorkDetailsName (ItemRemark)
      let description = "";
      
      if (item.WorkItemName) {
        description = item.WorkItemName;
      }
      
      if (item.WorkDetailsName || item.Description) {
        description += (description ? " / " : "") + (item.WorkDetailsName || item.Description);
      }
      
      if (item.ItemRemark && item.ItemRemark.trim() !== "") {
        description += ` (${item.ItemRemark})`;
      }
      
      if (isIGST) {
        excelData.push([
          index + 1,
          description,
          item.HSCODE || '',
          item.QAUOM || item.UOM || '',
          1,
          parseFloat(item.TaxableAmount || 0).toFixed(2),
          parseFloat(item.TaxableAmount || 0).toFixed(2),
          `${item.GSTRate || 0}%`,
          parseFloat(item.IGSTAmount || 0).toFixed(2),
          parseFloat(item.TotalAmount || 0).toFixed(2)
        ]);
      } else {
        excelData.push([
          index + 1,
          description,
          item.HSCODE || '',
          item.QAUOM || item.UOM || '',
          1,
          parseFloat(item.TaxableAmount || 0).toFixed(2),
          parseFloat(item.TaxableAmount || 0).toFixed(2),
          `${item.GSTRate || 0}%`,
          parseFloat(item.CGSTAmount || 0).toFixed(2),
          parseFloat(item.SGSTAmount || 0).toFixed(2),
          parseFloat(item.TotalAmount || 0).toFixed(2)
        ]);
      }
    });
    
    // Calculate totals
    const totalTaxable = tableData.reduce((sum, item) => sum + (item.TaxableAmount ?? 0), 0);
    const totalCGST = tableData.reduce((sum, item) => sum + (item.CGSTAmount ?? 0), 0);
    const totalSGST = tableData.reduce((sum, item) => sum + (item.SGSTAmount ?? 0), 0);
    const totalIGST = tableData.reduce((sum, item) => sum + (item.IGSTAmount ?? 0), 0);
    const totalAmount = tableData.reduce((sum, item) => sum + (item.TotalAmount ?? 0), 0);
    
    // Add totals row
    if (isIGST) {
      excelData.push([
        '',
        'Total:',
        '',
        '',
        '',
        '',
        totalTaxable.toFixed(2),
        '',
        totalIGST.toFixed(2),
        totalAmount.toFixed(2)
      ]);
    } else {
      excelData.push([
        '',
        'Total:',
        '',
        '',
        '',
        '',
        totalTaxable.toFixed(2),
        '',
        totalCGST.toFixed(2),
        totalSGST.toFixed(2),
        totalAmount.toFixed(2)
      ]);
    }
    
    // Add summary section
    excelData.push([]);
    excelData.push(['', '', '', '', '', 'Taxable Amount:', totalTaxable.toFixed(2)]);
    if (isIGST) {
      excelData.push(['', '', '', '', '', 'IGST Amount:', totalIGST.toFixed(2)]);
    } else {
      excelData.push(['', '', '', '', '', 'CGST Amount:', totalCGST.toFixed(2)]);
      excelData.push(['', '', '', '', '', 'SGST Amount:', totalSGST.toFixed(2)]);
    }
    excelData.push(['', '', '', '', '', 'Round Off:', parseFloat(quotationData.QAPrint?.RoundOff || 0).toFixed(2)]);
    excelData.push(['', '', '', '', '', 'Grand Total:', parseFloat(quotationData.QAPrint?.TotalAmount || totalAmount).toFixed(2)]);
    
    // Terms & Conditions
    if (quotationData.QATermsAndCondition && quotationData.QATermsAndCondition.length > 0) {
      excelData.push([]);
      excelData.push(['Terms & Conditions:']);
      quotationData.QATermsAndCondition.forEach((term, index) => {
        excelData.push([`${index + 1}. ${term.Name || ''}`]);
      });
    }
    
    // ============================================================
    // 🔹 ANNEXURE SECTION - QAPrintItems (Detailed Items) - SAME SHEET
    // ============================================================
    
    // Debug: Log the entire quotationData structure
    console.log('Full quotationData:', quotationData);
    console.log('QAPrintItems:', quotationData.QAPrintItems);
    console.log('QAPrintItems type:', typeof quotationData.QAPrintItems);
    console.log('QAPrintItems is array:', Array.isArray(quotationData.QAPrintItems));
    
    // Check if QAPrintItems exists and has data
    const hasQAPrintItems = quotationData.QAPrintItems && 
                            Array.isArray(quotationData.QAPrintItems) && 
                            quotationData.QAPrintItems.length > 0;
    
    console.log('hasQAPrintItems:', hasQAPrintItems);
    
    if (hasQAPrintItems) {
      console.log('Starting to add Annexure section to same sheet...');
      
      // Add spacing before annexure
      excelData.push([]);
      excelData.push([]);
      
      // Annexure Header
      excelData.push(['ANNEXURE - DETAILED ITEMS']);
      excelData.push([]);
      
      // Group by WorkName (same as PDF uses QAChildDetailName, but data has WorkName)
      const groupedSections = quotationData.QAPrintItems.reduce((acc, row) => {
        const groupKey = row.WorkName || row.QAChildDetailName || "Uncategorized";
        if (!acc[groupKey]) acc[groupKey] = [];
        acc[groupKey].push(row);
        return acc;
      }, {});
      
      console.log('Grouped sections:', groupedSections);
      console.log('Number of sections:', Object.keys(groupedSections).length);
      
      let continuousSlNo = 1;
      let allSubtotals = []; // Track all subtotals for grand total
      
      // Iterate over grouped sections
      Object.entries(groupedSections).forEach(([sectionName, rows]) => {
        // Section Header (WorkName like "Interior", "Electrical", etc.)
        excelData.push([sectionName]);
        excelData.push(['Sl.No', 'Category', 'UOM', 'QTY', 'Rate', 'Total']);
        
        // Add items
        rows.forEach((row) => {
          // Build description: WorkItemName / WorkDetailsName (ItemRemark)
          let description = "";
          
          // Use WorkItemName (like "PARTITION", "FALSE CEILING", etc.)
          if (row.WorkItemName) {
            description = row.WorkItemName;
          }
          
          // Add WorkDetailsName
          if (row.WorkDetailsName) {
            description += (description ? " / " : "") + row.WorkDetailsName;
          }
          
          // Add ItemRemark if it exists
          if (row.ItemRemark && row.ItemRemark.trim() !== "") {
            description += ` (${row.ItemRemark})`;
          }
          
          excelData.push([
            continuousSlNo++,
            description,
            row.UOM || '',
            row.Quantity || 0,
            parseFloat(row.Rate || 0).toFixed(2),
            parseFloat(row.TaxableAmount || 0).toFixed(2)
          ]);
        });
        
        // Calculate subtotal for this section
        const subtotal = rows.reduce((sum, row) => sum + Number(row.TaxableAmount || 0), 0);
        allSubtotals.push(subtotal);
        
        // Add subtotal row
        excelData.push([
          '',
          'Sub Total',
          '',
          '',
          '',
          subtotal.toFixed(2)
        ]);
        
        // Add spacing between sections
        excelData.push([]);
      });
      
      // ============================================================
      // 🔹 GRAND TOTAL (Sum of all subtotals) - Same as PDF
      // ============================================================
      const grandTotalAmount = allSubtotals.reduce((sum, subtotal) => sum + subtotal, 0);
      
      // Add grand total row
      excelData.push([
        '',
        'GRAND TOTAL',
        '',
        '',
        '',
        grandTotalAmount.toFixed(2)
      ]);
      
      console.log('✅ Annexure section added to main sheet with', quotationData.QAPrintItems.length, 'items');
    } else {
      console.log('❌ No QAPrintItems data available. QAPrintItems:', quotationData.QAPrintItems);
    }
    
    // Create worksheet for main quotation (now includes annexure on same sheet)
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    
    // Set column widths
    ws['!cols'] = [
      { wch: 6 },   // Sl.No
      { wch: 50 },  // Description
      { wch: 10 },  // HSN
      { wch: 8 },   // UOM
      { wch: 6 },   // Qty
      { wch: 12 },  // Unit Price
      { wch: 12 },  // Taxable
      { wch: 8 },   // GST %
      { wch: 12 },  // CGST/IGST
      { wch: 12 },  // SGST (if not IGST)
      { wch: 12 }   // Total
    ];
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Quotation');
    
    // Generate filename
    const filename = `Quotation_${quotationData.QAPrint?.InvoiceNo || quotationData.QAPrint?.QuotationNumber || 'Export'}_${new Date().getTime()}.xlsx`;
    
    // Save file
    XLSX.writeFile(wb, filename);
    
    console.log('Excel file generated successfully');
  } catch (error) {
    console.error('Error generating Excel file:', error);
    alert('Error generating Excel file. Please try again.');
  }
};
